/**
 * API Endpoint Constants
 */

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export const API_ENDPOINTS = {
    AUTH: {
        LOGIN: '/auth/login',
        REGISTER: '/auth/register',
        VERIFY_OTP: '/auth/verify-otp',
        INITIATE_2FA: '/auth/2fa/initiate',
        VERIFY_2FA_SETUP: '/auth/2fa/verify',
        DISABLE_2FA: '/auth/2fa/disable',
        REFRESH_TOKEN: '/auth/refresh-token',
        LOGOUT: '/auth/logout',
        FORGOT_PASSWORD: '/auth/forgot-password',
        PARTNER_INVITE: '/auth/partner-invite',
        PARTNER_INVITE_ACCEPT: '/auth/partner-invite/accept',
        VERIFY_RESET_OTP: '/auth/verify-reset-otp',
        RESET_PASSWORD: '/auth/reset-password'
    },
    ACCOUNT: {
        ME: '/account/me',
        COMPLETE_PROFILE: '/account/complete-profile',
        UPDATE_PROFILE: '/account/profile',
        VERIFY_PHONE_INITIATE: '/account/verify-phone/initiate',
        VERIFY_PHONE_CONFIRM: '/account/verify-phone/confirm',
        VERIFY_EMAIL_INITIATE: '/account/verify-email/initiate',
        VERIFY_EMAIL_CONFIRM: '/account/verify-email/confirm',
        BIRTHDAY_STATUS: '/account/birthday-status'
    },
    USERS: {
        BASE: '/users',
        BY_ID: (id: string) => `/users/${id}`,
        CHANGE_PASSWORD: (id: string) => `/users/${id}/change-password`,
        USERNAME_HISTORY: (id: string) => `/users/${id}/username-history`
    },
    USER_PROFILE_DETAIL: {
        PROFILE_INFO: (id: string) => `/user-profile/${id}/profile-info`,
        AGENCY_INFO: (id: string) => `/user-profile/${id}/agency-info`,
        SEGMENTS: (id: string) => `/user-profile/${id}/segments`,
        SURVEYS: (id: string) => `/user-profile/${id}/surveys`,
        VIOLATIONS: (id: string) => `/user-profile/${id}/violations`,
        AGENCY_ACTIVITIES: (id: string) => `/user-profile/${id}/agency-activities`,
        REQUESTS: (id: string) => `/user-profile/${id}/requests`,
        LIVESTREAMS: (id: string) => `/user-profile/${id}/livestreams`,
        TASK_PROGRESS: (id: string) => `/user-profile/${id}/task-progress`,
        TASK_PROGRESS_MONTHS: (id: string) => `/user-profile/${id}/task-progress/months`,
        WALLET: (id: string) => `/user-profile/${id}/wallet`,
        INTERACTIONS: (id: string) => `/user-profile/${id}/interactions`,
        INTERACTION_BY_ID: (userId: string, interactionId: string) => `/user-profile/${userId}/interactions/${interactionId}`,
        STATUS: (id: string) => `/user-profile/${id}/status`,
        QUICK_INFO: (id: string) => `/user-profile/${id}/quick-info`,
    },
    SYSTEM: {
        SETTINGS: '/system/settings'
    },
    ACCESS_CONTROL: {
        POLICIES: {
            BASE: '/policies',
            BY_ID: (id: string) => `/policies/${id}`
        },
        ROLES: {
            BASE: '/roles',
            BY_ID: (id: string) => `/roles/${id}`,
            PERMISSIONS: (id: string) => `/roles/${id}/permissions`
        },
        PERMISSIONS: '/permissions',
        USER_OVERRIDES: {
            BASE: '/permissions/users',
            BY_USER: (userId: string) => `/permissions/users/${userId}`
        }
    },
    NOTIFICATIONS: {
        INBOX: '/notifications/inbox',
        UNREAD_COUNT: '/notifications/inbox/unread-count',
        READ: '/notifications/inbox/read',
        TEMPLATES: {
            BASE: '/notifications/templates',
            BY_ID: (id: string) => `/notifications/templates/${id}`,
            BY_CODE: (code: string) => `/notifications/templates/${code}`
        },
        BROADCASTS: {
            // v2 cutover sonrası kalan tek canlı uç — kitle tahmini (AudienceTargeting).
            ESTIMATE_AUDIENCE: '/notifications/broadcasts/estimate-audience'
        }
    },
    METADATA: {
        BASE: '/metadata',
        ATTRIBUTES: '/metadata/attributes',
        UI_DEFINITIONS: '/metadata/ui-definitions',
        REQUEST_TYPES: '/metadata/request-types',
        MANAGERS: '/metadata/managers',
        STREAM_CATEGORIES: '/metadata/stream-categories',
        SPEL_SCHEMA: '/metadata/spel-schema',
        NOTIFICATION_CODES: '/metadata/notification-codes'
    },
    NOTIFICATION_PREFERENCES: {
        BASE: '/notification-preferences',
        ME: '/notification-preferences/me'
    },
    REPORTS: {
        BASE: '/reports'
    },
    SEGMENTS: {
        BASE: '/segments',
    },
    REQUESTS: {
        BASE: '/requests',
        BY_ID: (id: string) => `/requests/${id}`,
        ME: '/requests/me',
        REVIEW_NOTE: (id: string) => `/requests/${id}/review-note`,
        TYPES: '/requests/types',
        RULE_STATUS: (typeCode: string) => `/validations/request-types/${encodeURIComponent(typeCode)}/rule-status`
    },
    EQUIPMENTS: {
        BASE: '/equipments',
        CATALOG: '/equipments/catalog',
        MY_ASSIGNMENTS: '/equipments/my-assignments'
    },
    VIOLATIONS: {
        BASE: '/violations',
    },
    VIOLATION_APPEAL_TEMPLATES: {
        BASE: '/violation-appeal-templates',
    },
    AGENCY_ACTIVITIES: {
        BASE: '/agency-activities',
        DETAIL: (id: string) => `/agency-activities/${id}`,
        BRACKET: (id: string) => `/agency-activities/${id}/bracket`,
        RANKLIST: (id: string) => `/agency-activities/${id}/ranklist`,
        SCRAPE_NOW: '/agency-activities/scrape-now',
        REWARD_PARTICIPANTS: (id: string) => `/agency-activities/${id}/reward-participants`,
        REWARDS: (id: string) => `/agency-activities/${id}/rewards`,
    },
    SNAPSHOTS: {
        INGEST: '/snapshots'
    },
    INCENTIVES: {
        ROWS: '/incentives/rows',
        SUMMARY: '/incentives/summary',
        MONTHS: '/incentives/months',
        NOTIFY: '/incentives/notify',
        TEMPLATES: '/incentives/templates',
        PREVIEW: '/incentives/preview'
    },
    PANEL: {
        TRIAGE: '/panel/triage',
        PULSE: '/panel/pulse',
        ITEM_MUTE: '/panel/items/mute',
        ADMIN_SUMMARY: '/panel/admin/summary',
        ADMIN_HEALTH: '/panel/admin/health',
        ADMIN_MANAGERS: '/panel/admin/managers',
        ADMIN_FLOW: '/panel/admin/flow'
    },
    SCRAPE: {
        CREATOR_PROFILE_TRIGGER: '/scrape/creator-profile/trigger'
    },
    SCOUT_LEADS: {
        BASE: '/scout-leads',
        STAGE_COUNTS: '/scout-leads/stage-counts'
    },
    CREATOR_LEADS: {
        BASE: '/creator-leads',
        IMPORT: '/creator-leads/import',
        ELIGIBLE_MANAGERS: '/creator-leads/eligible-managers',
        BULK_ASSIGN: '/creator-leads/bulk-assign',
        REASSIGN_ORPHANS: '/creator-leads/reassign-orphans',
        OUTREACH_JOBS: '/creator-leads/outreach-jobs',
        OUTREACH_JOB: (jobId: string) => `/creator-leads/outreach-jobs/${jobId}`,
        OUTREACH_ACCOUNTS: '/creator-leads/outreach-accounts',
        OUTREACH_ACCOUNTS_VERIFY: '/creator-leads/outreach-accounts/verify',
        OUTREACH_ACCOUNTS_2FA_SEND: '/creator-leads/outreach-accounts/2fa/send',
        OUTREACH_ACCOUNTS_2FA_VERIFY: '/creator-leads/outreach-accounts/2fa/verify',
        OUTREACH_ACCOUNTS_ELIGIBILITY_POOL: '/creator-leads/outreach-accounts/eligibility-pool',
        OUTREACH_AGENTS: '/creator-leads/outreach-agents',
        MESSAGE_TEMPLATES: '/creator-leads/message-templates',
        MESSAGE_TEMPLATE_BY_ID: (id: string) => `/creator-leads/message-templates/${id}`,
        INBOX: '/creator-leads/inbox',
        INBOX_THREAD: '/creator-leads/inbox/thread',
        INBOX_REPLY: '/creator-leads/inbox/reply',
        BULK_ELIGIBILITY_CHECK: '/creator-leads/eligibility/bulk-check',
        AI_ANALYZE: '/creator-leads/ai-analyze',
        AI_ANALYZE_JOB: (jobId: string) => `/creator-leads/ai-analyze/${jobId}`
    },
    LOCATION: {
        BASE: '/locations',
        PROVINCES: '/locations/provinces',
        DISTRICTS: '/locations/provinces/{provinceId}/districts',
        SYNC: '/locations/sync'
    },
    ENGAGEMENT: {
        GATE: '/engagement/gate'
    },
    ANNOUNCEMENT: {
        BASE: '/announcements',
        BY_ID: (id: string) => `/announcements/${id}`,
        PUBLISH: (id: string) => `/announcements/${id}/publish`,
        TEST: (id: string) => `/announcements/${id}/test`,
        STATS: (id: string) => `/announcements/${id}/stats`,
        ARCHIVE: (id: string) => `/announcements/${id}/archive`,
        RESOLVE_AUDIENCE: '/announcements/audience/resolve',
        CARD_SEEN: (token: string) => `/announcements/card/${token}/seen`,
        CARD_ACTED: (token: string) => `/announcements/card/${token}/acted`,
        CARD_DISMISS: (token: string) => `/announcements/card/${token}/dismiss`,
        PUBLIC_PAGE: (token: string) => `/public/a/${token}`
    },
    SURVEY: {
        BASE: '/surveys',
        BY_ID: (id: string) => `/surveys/${id}`,
        PUBLISH: (id: string) => `/surveys/${id}/publish`,
        CLOSE: (id: string) => `/surveys/${id}/close`,
        ARCHIVE: (id: string) => `/surveys/${id}/archive`,
        RESULTS: (id: string) => `/surveys/${id}/results`,
        RESPONSES: (id: string) => `/surveys/${id}/responses`,
        AUDIENCE_PREVIEW: (id: string) => `/surveys/${id}/audience/preview`,
        ASSIGNED: '/surveys/assigned',
        FORM: (id: string) => `/surveys/${id}/form`,
        MY_RESPONSE: (id: string) => `/surveys/${id}/responses/mine`,
        RESPONSE_DRAFT: (id: string) => `/surveys/${id}/responses/draft`,
        RESPONSE_SUBMIT: (id: string) => `/surveys/${id}/responses/submit`,
        GATE_PENDING: '/surveys/gate/pending',
        SEEN: (id: string) => `/surveys/${id}/seen`
    },
    PRESENCE: {
        ONLINE_USERS: '/presence/online-users'
    },
    FILES: {
        UPLOAD: '/files/upload',
        DOWNLOAD: '/files/download'
    },
    I18N: {
        PUBLIC: '/i18n/public',
        PUBLIC_VERSION: '/i18n/public/version'
    },
    ELIGIBILITY: {
        CHECK: '/eligibility/check',
        HISTORY: '/eligibility/history'
    },
    ELIGIBILITY_WATCHLIST: {
        BASE: '/eligibility/watchlist',
        CHECK_NOW: '/eligibility/watchlist/check-now',
        SUMMARY: '/eligibility/watchlist/summary'
    },
    EARNINGS_DISTRIBUTION: {
        BASE: '/earnings-distribution',
        MONTHS: '/earnings-distribution/months',
        CONFIRM: '/earnings-distribution/confirm',
        CREDIT: '/earnings-distribution/credit',
    },
    PARTNER: {
        // Portal (partner:portal)
        ELIGIBILITY_CHECK: '/partner/eligibility-check',
        REFERRALS: '/partner/referrals',
        ME: '/partner/me',
        CREATORS: '/partner/creators',
        RECENT_CHECKS: '/partner/recent-checks',
        EARNINGS_MONTHS: '/partner/earnings/months',
        EARNINGS_MONTH: (yyyymm: string) => `/partner/earnings/months/${yyyymm}`,
        // Yönetim (partner:manage)
        MANAGE: '/manage/partners',
        MANAGE_SUMMARY: '/manage/partners/summary',
        MANAGE_BY_ID: (id: string) => `/manage/partners/${id}`,
        MANAGE_SUSPEND: (id: string) => `/manage/partners/${id}/suspend`,
        MANAGE_ACTIVATE: (id: string) => `/manage/partners/${id}/activate`,
        MANAGE_RESEND: (id: string) => `/manage/partners/${id}/resend-invite`,
        MANAGE_CREATORS: (id: string) => `/manage/partners/${id}/creators`,
        MANAGE_PAYOUTS: (id: string) => `/manage/partners/${id}/payouts`,
        MANAGE_PAID: (id: string, yyyymm: string) => `/manage/partners/${id}/payouts/${yyyymm}/paid`,
        MANAGE_CONFLICTS: (id: string) => `/manage/partners/${id}/conflicts`,
        MANAGE_RESOLVE: (referralId: string) => `/manage/partners/conflicts/${referralId}`,
        MANAGE_AWAITING: '/manage/partners/referrals/awaiting',
        MANAGE_APPROVE_REFERRAL: (referralId: string) => `/manage/partners/referrals/${referralId}/approve`,
        MANAGE_REJECT_REFERRAL: (referralId: string) => `/manage/partners/referrals/${referralId}/reject`,
        PLANS: '/manage/partner-plans',
        PLAN_BY_ID: (id: string) => `/manage/partner-plans/${id}`,
        PLAN_COPY: (id: string) => `/manage/partner-plans/${id}/copy`,
        PLAN_SIMULATE: '/manage/partner-plans/simulate',
    },
    REFERRALS: {
        BASE: '/referrals',
        BY_USER: (userId: string) => `/referrals/user/${userId}`,
        // Ters yön: bu kullanıcıyı KİM referans etti (BY_USER'ın tersi).
        REFERRER_OF: (userId: string) => `/referrals/user/${userId}/referrer`,
        // Kişi başı kota: kaç kullanıldı / en fazla kaç (sınır panelde, ön yüzde yok).
        QUOTA: (userId: string) => `/referrals/user/${userId}/quota`,
        BY_ID: (id: string) => `/referrals/${id}`,
        CANCEL: (id: string) => `/referrals/${id}/cancel`,
        ASSIGN: '/referrals',
        ASSIGN_PREVIEW: '/referrals/preview',
    },
    MANAGER_MAPPINGS: {
        BASE: '/manager-mappings',
        BY_ID: (id: string) => `/manager-mappings/${id}`,
        UNMAPPED_MANAGERS: '/manager-mappings/unmapped-managers',
        ORPHAN_EMAILS: '/manager-mappings/orphan-emails',
    },
    GOAL_TEMPLATES: {
        BASE: '/goal-templates',
        BY_ID: (id: string) => `/goal-templates/${id}`,
    },
    GOAL_INSTANCES: {
        BASE: '/goal-instances',
        ME: '/goal-instances/me',
        BY_USER: (userId: string) => `/goal-instances/user/${userId}`,
        SUMMARY_MANAGERS: '/goal-instances/summary/managers',
        OVERRIDE: (id: string) => `/goal-instances/${id}/override`,
        RECOMPUTE: (id: string) => `/goal-instances/${id}/recompute`,
    },
    GOAL_ASSIGNMENTS: {
        BASE: '/goal-assignments',
        BY_ID: (id: string) => `/goal-assignments/${id}`,
        BY_TEMPLATE: (templateId: string) => `/goal-assignments/template/${templateId}`,
    }
}


/**
 * Public endpoints that don't require authentication
 */
export const PUBLIC_ENDPOINTS = [
    API_ENDPOINTS.AUTH.LOGIN,
    API_ENDPOINTS.AUTH.REGISTER,
    API_ENDPOINTS.AUTH.VERIFY_OTP,
    API_ENDPOINTS.AUTH.REFRESH_TOKEN,
    API_ENDPOINTS.AUTH.FORGOT_PASSWORD,
    API_ENDPOINTS.AUTH.VERIFY_RESET_OTP,
    API_ENDPOINTS.AUTH.RESET_PASSWORD,
    API_ENDPOINTS.I18N.PUBLIC,
    API_ENDPOINTS.I18N.PUBLIC_VERSION
]
